import { useQuery } from '@tanstack/react-query';
import type { Matchday, Round, Translated } from '@uefadigital/panenka';
import { MatchDayType, RoundMode } from '@uefadigital/panenka';
import { PkIcon, PkIdentifier } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { memo, useCallback, useMemo } from 'react';

import { useVideoPlaylistOptions, useVideoPlaylistStore } from '../../store';

import { useWindowSize } from '@hooks/useWindowSize';
import { getCompetitionStructureQuery } from '@queries/competition';
import { hydrateWithTranslations } from '@services/TranslatorService';
import ListFilterModal from '@shared/ListFilterModal';
import { useTranslateFn } from '@shared/Translate';
import { orderBy } from '@utilities/collection-utils';

import * as classes from './styles.module.scss';

const customDateFormat = 'YYYY-MM-DDTHH:mm:ssZZ';
const getDateLabel = (dateFrom: string, dateTo: string) => {
  const from = dayjs(dateFrom, customDateFormat).add(12, 'h');
  const to = dayjs(dateTo, customDateFormat).add(12, 'h');
  if (from.isSame(to, 'date')) return `${from.format('DD MMMM')}`;

  if (from.isSame(to, 'month')) return `${from.format('DD')} - ${to.format('DD MMM')}`;

  return `${from.format('DD MMM')} - ${to.format('DD MMM')}`;
};

const MathdayIdentifier = ({
  matchday,
  rounds,
  onSelect,
  selected,
}: {
  matchday: Translated<Matchday>;
  rounds: Translated<Round>[];
  onSelect: (selected: boolean) => void;
  selected: boolean;
}) => {
  const t = useTranslateFn();
  const round = useMemo(() => rounds.find((r) => r.id === matchday.roundId), [matchday.roundId, rounds]);

  const secondaryLabel = useMemo(() => {
    let mdName = '';
    if (round?.mode === RoundMode.GROUP) {
      mdName = matchday.longName;
    }

    if (round?.mode === RoundMode.KNOCK_OUT && round.modeDetail === 'KNOCK_OUT_TWO_LEGS') {
      mdName = t(matchday?.type === MatchDayType.FIRST_LEG ? '1stleg_abbr' : '2ndleg_abbr');
    }

    const dateLabel = getDateLabel(matchday.dateFrom, matchday.dateTo);

    return mdName ? `${mdName} (${dateLabel})` : dateLabel;
  }, [matchday, round, t]);

  return (
    <PkIdentifier
      className={clsx('pk-py--xs', classes.identifier)}
      enableSelection
      selected={selected}
      onPkChange={(e) => onSelect(e.detail.selected)}
    >
      {round && <span slot='primary'>{round.name}</span>}
      <span slot='secondary'>{secondaryLabel}</span>
    </PkIdentifier>
  );
};

const MatchdayFilter = () => {
  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  const { options } = useVideoPlaylistOptions();
  const selectedMatchday = useVideoPlaylistStore((state) => state.filters?.matchday);
  const setMatchday = useVideoPlaylistStore((state) => state.setMatchday);

  const buttonLabel = t('matchday');

  const { data: compStructure } = useQuery(
    getCompetitionStructureQuery({
      competitionId: window.competitionId,
      seasonYears: options.season ?? 'current',
      phase: options.phase,
    }),
  );

  const rounds = useMemo(
    () =>
      orderBy(
        compStructure?.rounds?.map((r) => hydrateWithTranslations(r)),
        'orderInCompetition',
      ) ?? [],
    [compStructure],
  );

  const matchdays = useMemo(
    () =>
      orderBy(
        compStructure?.matchdays?.map((md) => hydrateWithTranslations(md)),
        (md) => -rounds.find((r) => r.id === md.roundId)?.orderInCompetition,
      ),
    [compStructure, rounds],
  );

  const handleSelect = useCallback(
    (md: Translated<Matchday>[]) => {
      setMatchday(md?.at(0));
    },
    [setMatchday],
  );

  const renderButton = () => (
    <>
      {(isMobile || !selectedMatchday) && (
        <>
          {buttonLabel}
          <PkIcon className='pk-ml--xs' name='actions-filter' size={24} />
        </>
      )}
      {!isMobile && selectedMatchday && selectedMatchday.longName}
    </>
  );

  const renderItem = ({ entity: matchday, selected, onSelect }) => (
    <MathdayIdentifier matchday={matchday} onSelect={onSelect} selected={selected} rounds={rounds} />
  );

  return (
    <ListFilterModal<Translated<Matchday>>
      headerText={buttonLabel}
      onSelected={handleSelect}
      entities={matchdays}
      selectedEntities={selectedMatchday ? [selectedMatchday] : []}
      dialogClassName={classes.matchdayModal}
      buttonClassName={classes.button}
      ButtonContent={renderButton}
      ItemComponent={renderItem}
    />
  );
};

export default memo(MatchdayFilter);
